import {
  formatLanguageDate,
  formatLanguageNumber,
  formatLanguagePercent,
  getIntlNumberFormat,
  type LanguageNumberFormatOptions,
} from "~/lib/language-lens/intl";

export type NumericInput = bigint | number | string | null | undefined;
type DateTimeInput = Date | number | string | null | undefined;

interface BaseFormatOptions {
  fallback?: string;
}

export interface FormatNumberOptions extends BaseFormatOptions {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  showSign?: boolean;
  useGrouping?: boolean;
}

export interface FormatCurrencyOptions extends BaseFormatOptions {
  /**
   * ISO 4217 code for the display currency (e.g. "TRY", "BRL").
   * Defaults to USD. Display syntax only — callers are responsible for
   * passing values already denominated in this currency; no conversion
   * happens here.
   */
  currency?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  showSign?: boolean;
}

export interface FormatCompactCurrencyOptions extends FormatCurrencyOptions {
  compactSuffixCase?: "lower" | "upper";
}

export interface CurrencyParts {
  /** Leading currency syntax, including the sign for prefix-style locales. */
  prefix: string;
  /** Formatted numeric value without currency affixes. */
  value: string;
  /** Trailing locale spacing plus currency symbol for suffix-style locales. */
  suffix: string;
}

interface CurrencyAffixes {
  readonly prefix: string;
  readonly suffix: string;
}

export interface FormatPercentOptions extends BaseFormatOptions {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  showSign?: boolean;
}

export interface FormatDateTimeOptions extends BaseFormatOptions {
  dateStyle?: "full" | "long" | "medium" | "short";
  timeStyle?: "full" | "long" | "medium" | "short";
}

export interface FormatAddressOptions extends BaseFormatOptions {
  endChars?: number;
  startChars?: number;
}

const DEFAULT_FALLBACK = "—";
const FIXED_NUMBER_FORMAT_LOCALE = "en-US";
const DEFAULT_COMPACT_FRACTION_DIGITS = 1;
const COMPACT_SCALES = [
  { suffix: "", threshold: 1 },
  { suffix: "k", threshold: 1_000 },
  { suffix: "m", threshold: 1_000_000 },
  { suffix: "b", threshold: 1_000_000_000 },
  { suffix: "t", threshold: 1_000_000_000_000 },
] as const;
const FIXED_NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();
const CURRENCY_MINOR_UNITS = new Map<string, number>();
const CURRENCY_SYMBOL_OVERRIDES: Readonly<Record<string, string | undefined>> = {
  ARS: "AR$",
  BTC: "₿",
  MXN: "MX$",
};
const CUSTOM_CURRENCY_SYMBOL_TEMPLATE = "XXX";

function toFixedNumberFormatterKey(
  locale: string,
  options: Intl.NumberFormatOptions,
): string {
  return [
    locale,
    options.style ?? "decimal",
    options.currency ?? "",
    options.currencyDisplay ?? "",
    options.minimumFractionDigits ?? "",
    options.maximumFractionDigits ?? "",
    options.useGrouping === false ? "nogroup" : "group",
  ].join(":");
}

export function toFiniteNumber(value: NumericInput): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "bigint") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getFallback(options?: BaseFormatOptions): string {
  return options?.fallback ?? DEFAULT_FALLBACK;
}

function formatFixedLocaleNumber(
  value: number,
  options: Intl.NumberFormatOptions,
  locale = FIXED_NUMBER_FORMAT_LOCALE,
): string {
  return getFixedLocaleNumberFormatter(locale, options).format(value);
}

function getFixedLocaleNumberFormatter(
  locale: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const key = toFixedNumberFormatterKey(locale, options);
  const cached = FIXED_NUMBER_FORMATTERS.get(key);
  if (cached !== undefined) {
    return cached;
  }

  /* i18n-exempt: explicitly fixed-locale behavior-stable output. */
  const formatter = new Intl.NumberFormat(locale, options);
  FIXED_NUMBER_FORMATTERS.set(key, formatter);
  return formatter;
}

function getNumberSign(value: number, showPositiveSign = false): string {
  if (value < 0) {
    return "-";
  }

  if (showPositiveSign && value > 0) {
    return "+";
  }

  return "";
}

function parseFormattedNumber(parts: Intl.NumberFormatPart[]): number {
  const integer = parts
    .filter((part) => part.type === "integer")
    .map((part) => part.value)
    .join("");
  const fraction = parts.find((part) => part.type === "fraction")?.value;
  return Number(fraction === undefined ? integer : `${integer}.${fraction}`);
}

function formatNumberLocaleCompactMagnitude(
  value: number,
  options?: {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    prefix?: string;
    suffix?: string;
    compactSuffixCase?: "lower" | "upper";
    showSign?: boolean;
  },
): string {
  const sign = getNumberSign(value, options?.showSign === true);
  const absoluteValue = Math.abs(value);
  const maximumFractionDigits =
    options?.maximumFractionDigits ?? DEFAULT_COMPACT_FRACTION_DIGITS;
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;
  let scaleIndex = 0;

  for (let index = COMPACT_SCALES.length - 1; index > 0; index -= 1) {
    const candidateScale = COMPACT_SCALES[index];
    if (candidateScale && absoluteValue >= candidateScale.threshold) {
      scaleIndex = index;
      break;
    }
  }

  while (scaleIndex > 0 && scaleIndex < COMPACT_SCALES.length - 1) {
    const currentScale = COMPACT_SCALES[scaleIndex];
    if (!currentScale) {
      break;
    }

    const roundedScaledValue = parseFormattedNumber(
      getIntlNumberFormat({
        maximumFractionDigits,
        minimumFractionDigits,
      }).formatToParts(absoluteValue / currentScale.threshold),
    );

    if (roundedScaledValue < 1000) {
      break;
    }

    scaleIndex += 1;
  }

  const selectedScale = COMPACT_SCALES[scaleIndex];
  if (!selectedScale) {
    const formatted = formatLanguageNumber(absoluteValue, {
      maximumFractionDigits,
      minimumFractionDigits,
    });
    return `${sign}${options?.prefix ?? ""}${formatted}${options?.suffix ?? ""}`;
  }

  if (selectedScale.suffix.length === 0) {
    const formatted = formatLanguageNumber(absoluteValue, {
      maximumFractionDigits,
      minimumFractionDigits,
    });
    return `${sign}${options?.prefix ?? ""}${formatted}${options?.suffix ?? ""}`;
  }

  const scaledValue = absoluteValue / selectedScale.threshold;
  const formattedScaledValue = formatLanguageNumber(scaledValue, {
    maximumFractionDigits,
    minimumFractionDigits,
  });

  const compactSuffix =
    options?.compactSuffixCase === "upper"
      ? selectedScale.suffix.toUpperCase()
      : selectedScale.suffix;
  return `${sign}${options?.prefix ?? ""}${formattedScaledValue}${compactSuffix}${options?.suffix ?? ""}`;
}

function roundToMaximumFractionDigits(
  value: number,
  maximumFractionDigits: number,
): number {
  return Number(value.toFixed(maximumFractionDigits));
}

function formatLanguageCompactMagnitude(
  value: number,
  options?: Pick<
    FormatNumberOptions,
    "maximumFractionDigits" | "minimumFractionDigits" | "showSign"
  >,
): string {
  const sign = getNumberSign(value, options?.showSign === true);
  const absoluteValue = Math.abs(value);
  const maximumFractionDigits =
    options?.maximumFractionDigits ?? DEFAULT_COMPACT_FRACTION_DIGITS;
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;
  let scaleIndex = 0;

  for (let index = COMPACT_SCALES.length - 1; index > 0; index -= 1) {
    const candidateScale = COMPACT_SCALES[index];
    if (candidateScale && absoluteValue >= candidateScale.threshold) {
      scaleIndex = index;
      break;
    }
  }

  while (scaleIndex > 0 && scaleIndex < COMPACT_SCALES.length - 1) {
    const currentScale = COMPACT_SCALES[scaleIndex];
    if (!currentScale) {
      break;
    }

    const roundedScaledValue = roundToMaximumFractionDigits(
      absoluteValue / currentScale.threshold,
      maximumFractionDigits,
    );
    if (roundedScaledValue < 1000) {
      break;
    }

    scaleIndex += 1;
  }

  const selectedScale = COMPACT_SCALES[scaleIndex];
  if (!selectedScale || selectedScale.suffix.length === 0) {
    return `${sign}${formatLanguageNumber(absoluteValue, {
      maximumFractionDigits,
      minimumFractionDigits,
    })}`;
  }

  return `${sign}${formatLanguageNumber(
    absoluteValue / selectedScale.threshold,
    {
      maximumFractionDigits,
      minimumFractionDigits,
    },
  )}${selectedScale.suffix}`;
}

function getDefaultNumberMaximumFractionDigits(value: number): number {
  if (value < 0.000001) {
    return 12;
  }
  if (value < 0.0001) {
    return 8;
  }
  if (value < 0.01) {
    return 6;
  }
  if (value < 0.1) {
    return 4;
  }
  return 3;
}

function getNumberFormatOptions(
  value: number,
  options?: FormatNumberOptions,
): LanguageNumberFormatOptions {
  const hasExplicitFormattingOptions =
    options?.maximumFractionDigits !== undefined ||
    options?.minimumFractionDigits !== undefined ||
    options?.useGrouping !== undefined;

  return {
    maximumFractionDigits: hasExplicitFormattingOptions
      ? (options?.maximumFractionDigits ?? 2)
      : getDefaultNumberMaximumFractionDigits(value),
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    useGrouping: options?.useGrouping ?? true,
  };
}

function formatPercentInternal(
  value: number,
  options?: FormatPercentOptions,
): string {
  return formatLanguagePercent(value / 100, {
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    signDisplay: options?.showSign === true ? "exceptZero" : "auto",
  });
}

function getCurrencyMinorUnits(currency: string): number {
  const cached = CURRENCY_MINOR_UNITS.get(currency);
  if (cached !== undefined) {
    return cached;
  }

  let minorUnits = 2;
  try {
    /* i18n-exempt: fixed en-US lookup preserves currency minor-unit behavior. */
    minorUnits = new Intl.NumberFormat("en-US", {
      currency,
      style: "currency",
    }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    // Preserve the historical two-decimal default for invalid currency codes.
  }

  CURRENCY_MINOR_UNITS.set(currency, minorUnits);
  return minorUnits;
}

function getDefaultCurrencyFractionDigits(
  absoluteValue: number,
  currency: string,
): number {
  if (
    getCurrencyMinorUnits(currency) === 0 &&
    (absoluteValue === 0 || absoluteValue >= 1)
  ) {
    return 0;
  }

  if (absoluteValue === 0) {
    return 2;
  }

  if (absoluteValue < 0.01) {
    return 6;
  }

  if (absoluteValue < 0.1) {
    return 4;
  }

  return 2;
}

function getCurrencySymbolOverride(currency: string): string | undefined {
  return CURRENCY_SYMBOL_OVERRIDES[currency];
}

function formatOverriddenCurrencyAbsoluteValue(params: {
  absoluteValue: number;
  affixes: CurrencyAffixes;
  maximumFractionDigits: number;
  minimumFractionDigits: number;
}): string {
  const numericValue = formatLanguageNumber(params.absoluteValue, {
    maximumFractionDigits: params.maximumFractionDigits,
    minimumFractionDigits: params.minimumFractionDigits,
  });

  return `${params.affixes.prefix}${numericValue}${params.affixes.suffix}`;
}

export function formatCurrency(
  value: NumericInput,
  options?: FormatCurrencyOptions,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  const sign =
    options?.showSign === true
      ? numericValue > 0
        ? "+"
        : numericValue < 0
          ? "-"
          : ""
      : numericValue < 0
        ? "-"
        : "";
  const absoluteValue = Math.abs(numericValue);
  const currency = options?.currency ?? "USD";
  const minimumFractionDigits =
    options?.minimumFractionDigits ??
    getDefaultCurrencyFractionDigits(absoluteValue, currency);
  const maximumFractionDigits =
    options?.maximumFractionDigits ?? minimumFractionDigits;

  const currencySymbolOverride = getCurrencySymbolOverride(currency);

  if (currencySymbolOverride !== undefined) {
    return `${sign}${formatOverriddenCurrencyAbsoluteValue({
      absoluteValue,
      affixes: getCurrencyAffixes(currency),
      maximumFractionDigits,
      minimumFractionDigits,
    })}`;
  }

  return `${sign}${getIntlNumberFormat({
    currency,
    currencyDisplay: "symbol",
    maximumFractionDigits,
    minimumFractionDigits,
    style: "currency",
  }).format(absoluteValue)}`;
}

export function formatCurrencyParts(
  value: NumericInput,
  options?: FormatCurrencyOptions,
): CurrencyParts {
  const formatted = formatCurrency(value, options);
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return { prefix: "", value: formatted, suffix: "" };
  }

  const sign = getNumberSign(numericValue, options?.showSign === true);
  const absoluteValue = Math.abs(numericValue);
  const currency = options?.currency ?? "USD";
  const minimumFractionDigits =
    options?.minimumFractionDigits ??
    getDefaultCurrencyFractionDigits(absoluteValue, currency);
  const maximumFractionDigits =
    options?.maximumFractionDigits ?? minimumFractionDigits;
  const currencySymbolOverride = getCurrencySymbolOverride(currency);
  if (currencySymbolOverride !== undefined) {
    const formattedAbsoluteValue = formatLanguageNumber(absoluteValue, {
      maximumFractionDigits,
      minimumFractionDigits,
    });
    const affixes = getCurrencyAffixes(currency);

    if (affixes.prefix.length > 0) {
      return {
        prefix: `${sign}${affixes.prefix}`,
        value: formattedAbsoluteValue,
        suffix: affixes.suffix,
      };
    }

    return {
      prefix: "",
      value: `${sign}${formattedAbsoluteValue}`,
      suffix: affixes.suffix,
    };
  }

  const formattedAbsoluteValue = getIntlNumberFormat({
    currency,
    currencyDisplay: "symbol",
    maximumFractionDigits,
    minimumFractionDigits,
    style: "currency",
  }).format(absoluteValue);
  const affixes = getCurrencyAffixes(currency);

  if (
    affixes.prefix.length > 0 &&
    formattedAbsoluteValue.startsWith(affixes.prefix)
  ) {
    return {
      prefix: `${sign}${affixes.prefix}`,
      value: formattedAbsoluteValue.slice(affixes.prefix.length),
      suffix: "",
    };
  }

  if (
    affixes.suffix.length > 0 &&
    formattedAbsoluteValue.endsWith(affixes.suffix)
  ) {
    return {
      prefix: "",
      value: `${sign}${formattedAbsoluteValue.slice(0, -affixes.suffix.length)}`,
      suffix: affixes.suffix,
    };
  }

  return { prefix: "", value: formatted, suffix: "" };
}

/**
 * Minimum count of zeros after the decimal point before a price switches to
 * subscript notation. Above this, formatCurrency's 4-6 fraction digits stay
 * readable.
 */
const SUBSCRIPT_CURRENCY_ZERO_THRESHOLD = 4;
const SUBSCRIPT_CURRENCY_SIGNIFICANT_DIGITS = 4;

const UNICODE_SUBSCRIPT_DIGITS = "₀₁₂₃₄₅₆₇₈₉";

function toUnicodeSubscript(value: number): string {
  return [...String(value)]
    .map((char) => UNICODE_SUBSCRIPT_DIGITS[Number(char)] ?? char)
    .join("");
}

export interface SubscriptCurrencyParts {
  /**
   * Significant digits shown after the compressed zeros; empty when no
   * subscript is needed.
   */
  digits: string;
  /**
   * Text before the subscript ("-$0.0", sign included), or the fully
   * formatted price when no subscript is needed.
   */
  prefix: string;
  /** Locale spacing plus currency symbol after the significant digits. */
  readonly suffix: string;
  /**
   * Count of zeros compressed into the subscript — the TOTAL zero run
   * between the decimal point and the first significant digit, matching
   * the CoinGecko/DEXScreener/Birdeye convention (and this repo's
   * format-crypto-amount + legacy webapp): $0.0\u2085112 means five zeros
   * then "112". Null when the value formats fine without subscript.
   */
  subscriptZeroCount: number | null;
  /**
   * Plain-text rendering using Unicode subscript digits, e.g. "$0.0₁₁131".
   * Safe for tab titles, tooltips, and accessible labels.
   */
  text: string;
}

/**
 * Formats a currency price, compressing long runs of leading zeros into
 * CoinGecko-style subscript notation (0.000000000000131 -> $0.0₁₁131) so
 * micro-cap token prices stay legible instead of rendering as "$0.000000".
 */
export function formatSubscriptCurrencyParts(
  value: NumericInput,
  options?: FormatCurrencyOptions,
): SubscriptCurrencyParts {
  const plain = (): SubscriptCurrencyParts => {
    const text = formatCurrency(value, options);
    return {
      digits: "",
      prefix: text,
      suffix: "",
      subscriptZeroCount: null,
      text,
    };
  };

  const numericValue = toFiniteNumber(value);
  if (
    numericValue === null ||
    numericValue === 0 ||
    Math.abs(numericValue) >= 0.01
  ) {
    return plain();
  }

  // toFixed(20) sidesteps scientific notation for tiny magnitudes.
  const match = /^0\.(0*)([1-9]\d*)$/.exec(Math.abs(numericValue).toFixed(20));
  if (!match) {
    return plain();
  }

  const [, zeroGroup = "", digitGroup = ""] = match;
  const zeroRun = zeroGroup.length;
  if (zeroRun < SUBSCRIPT_CURRENCY_ZERO_THRESHOLD) {
    return plain();
  }

  const digits =
    digitGroup
      .slice(0, SUBSCRIPT_CURRENCY_SIGNIFICANT_DIGITS)
      .replace(/0+$/, "") || "0";
  const subscriptZeroCount = zeroRun;
  const currency = options?.currency ?? "USD";
  const decimalSeparator = getDecimalSeparator();
  if (decimalSeparator === null) {
    return plain();
  }
  const affixes = getCurrencyAffixes(currency);
  if (affixes.prefix.length === 0 && affixes.suffix.length === 0) {
    return plain();
  }
  const prefix = `${numericValue < 0 ? "-" : ""}${affixes.prefix}0${decimalSeparator}0`;
  const suffix = affixes.suffix;

  return {
    digits,
    prefix,
    suffix,
    subscriptZeroCount,
    text: `${prefix}${toUnicodeSubscript(subscriptZeroCount)}${digits}${suffix}`,
  };
}

export function getCurrencyAffixes(currency: string): CurrencyAffixes {
  const currencySymbolOverride = getCurrencySymbolOverride(currency);

  const parts = getIntlNumberFormat({
    // XXX renders Intl's generic currency symbol, so custom display symbols
    // inherit locale placement and spacing instead of code-style spacing.
    currency:
      currencySymbolOverride === undefined
        ? currency
        : CUSTOM_CURRENCY_SYMBOL_TEMPLATE,
    currencyDisplay: "symbol",
    style: "currency",
  }).formatToParts(0);
  const currencyIndex = parts.findIndex((part) => part.type === "currency");
  const firstIntegerIndex = parts.findIndex((part) => part.type === "integer");
  const lastNumericIndex = parts.reduce(
    (lastIndex, part, index) =>
      part.type === "integer" ||
      part.type === "group" ||
      part.type === "decimal" ||
      part.type === "fraction"
        ? index
        : lastIndex,
    -1,
  );
  const currencyPart = parts[currencyIndex];

  if (
    currencyPart === undefined ||
    firstIntegerIndex < 0 ||
    lastNumericIndex < 0
  ) {
    return { prefix: "", suffix: "" };
  }
  const currencySymbol = currencySymbolOverride ?? currencyPart.value;

  if (currencyIndex < firstIntegerIndex) {
    const spacing = parts
      .slice(currencyIndex + 1, firstIntegerIndex)
      .filter((part) => part.type === "literal")
      .map((part) => part.value)
      .join("");
    return { prefix: `${currencySymbol}${spacing}`, suffix: "" };
  }

  if (currencyIndex > lastNumericIndex) {
    const spacing = parts
      .slice(lastNumericIndex + 1, currencyIndex)
      .filter((part) => part.type === "literal")
      .map((part) => part.value)
      .join("");
    return { prefix: "", suffix: `${spacing}${currencySymbol}` };
  }

  return { prefix: "", suffix: "" };
}

function getDecimalSeparator(): string | null {
  return (
    getIntlNumberFormat({
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
      useGrouping: false,
    })
      .formatToParts(0.1)
      .find((part) => part.type === "decimal")?.value ?? null
  );
}

export function formatCompactCurrency(
  value: NumericInput,
  options?: FormatCompactCurrencyOptions,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  const currency = options?.currency ?? "USD";
  const affixes = getCurrencyAffixes(currency);

  return formatNumberLocaleCompactMagnitude(numericValue, {
    maximumFractionDigits: options?.maximumFractionDigits,
    minimumFractionDigits: options?.minimumFractionDigits,
    compactSuffixCase: options?.compactSuffixCase,
    prefix: affixes.prefix,
    showSign: options?.showSign,
    suffix: affixes.suffix,
  });
}

export function formatNumber(
  value: NumericInput,
  options?: FormatNumberOptions,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  const sign = getNumberSign(numericValue, options?.showSign === true);
  return `${sign}${formatLanguageNumber(
    Math.abs(numericValue),
    getNumberFormatOptions(numericValue, options),
  )}`;
}

/**
 * Fixed-locale number syntax for behavior-stable composition.
 * User-facing generic numbers must use `formatNumber` instead.
 */
export function formatFixedEnUsNumber(
  value: NumericInput,
  options?: FormatNumberOptions,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  const sign = getNumberSign(numericValue, options?.showSign === true);
  return `${sign}${formatFixedLocaleNumber(
    Math.abs(numericValue),
    getNumberFormatOptions(numericValue, options),
    FIXED_NUMBER_FORMAT_LOCALE,
  )}`;
}

export function formatCompactNumber(
  value: NumericInput,
  options?: FormatNumberOptions,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  return formatLanguageCompactMagnitude(numericValue, {
    maximumFractionDigits: options?.maximumFractionDigits,
    minimumFractionDigits: options?.minimumFractionDigits,
    showSign: options?.showSign,
  });
}

export function formatPercent(
  value: NumericInput,
  options?: Omit<FormatPercentOptions, "showSign">,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  return formatPercentInternal(numericValue, options);
}

export function formatSignedPercent(
  value: NumericInput,
  options?: FormatPercentOptions,
): string {
  const numericValue = toFiniteNumber(value);
  if (numericValue === null) {
    return getFallback(options);
  }

  return formatPercentInternal(numericValue, {
    ...options,
    showSign: true,
  });
}

export function formatQuantity(
  value: NumericInput,
  options?: BaseFormatOptions,
): string {
  return formatNumber(value, {
    fallback: getFallback(options),
    maximumFractionDigits: 6,
    minimumFractionDigits: 0,
  });
}

export function formatDateTime(
  value: DateTimeInput,
  options?: FormatDateTimeOptions,
): string {
  if (value === null || value === undefined) {
    return getFallback(options);
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return getFallback(options);
  }

  return formatLanguageDate(date, {
    dateStyle: options?.dateStyle ?? "medium",
    timeStyle: options?.timeStyle ?? "short",
  });
}

export function formatAddress(
  address: string | null | undefined,
  options?: FormatAddressOptions,
): string {
  if (typeof address !== "string" || address.trim().length === 0) {
    return getFallback(options);
  }

  const trimmedAddress = address.trim();
  const startChars = options?.startChars ?? 8;
  const endChars = options?.endChars ?? 6;
  if (trimmedAddress.length <= startChars + endChars + 3) {
    return trimmedAddress;
  }

  return `${trimmedAddress.slice(0, startChars)}...${trimmedAddress.slice(-endChars)}`;
}
